import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadRankingReport } from '../../services/ranking.service';

/** Lista de reportes disponibles — refleja el enum ReportNames del backend. */
const REPORTES = [
  {
    id: 'reporte_grau_gral',
    nombre: 'Reporte GRAU General',
    descripcion: 'Reporte general con todos los torneos computados',
  },
  {
    id: 'report_variaciones_sumadas',
    nombre: 'Variaciones sumadas',
    descripcion: 'Variaciones de cada torneo sumadas',
  },
  {
    id: 'report_nuevos_jugadores',
    nombre: 'Jugadores nuevos',
    descripcion: 'Nuevos jugadores rankeados',
  },
  {
    id: 'report_grau_activos_alfabetico',
    nombre: 'Activos alfabético',
    descripcion: 'Activos en orden alfabético',
  },
  {
    id: 'report_grau_activos_por_elo',
    nombre: 'Activos por GRAU',
    descripcion: 'Activos en orden según valor del GRAU',
  },
  {
    id: 'todos_extendido_2',
    nombre: 'Todos los jugadores',
    descripcion: 'Todos los jugadores, incluyendo no activos',
  },
];

export default function StepReportes({ rkgId, onBack, onSalir }) {
  // Estado de descarga por reporte: null | 'loading' | 'done'
  const [downloadState, setDownloadState] = useState({});

  const handleDownload = (reportId) => {
    setDownloadState((prev) => ({ ...prev, [reportId]: 'loading' }));
    // window.open es sincrónico; simulamos el fin con un timeout corto
    // para dar feedback visual y volver a estado normal
    downloadRankingReport(rkgId, reportId);
    setTimeout(() => {
      setDownloadState((prev) => ({ ...prev, [reportId]: 'done' }));
      // Restaurar ícono tras 2 s
      setTimeout(() => {
        setDownloadState((prev) => ({ ...prev, [reportId]: null }));
      }, 2000);
    }, 800);
  };

  const cellSx = {
    color: '#cbd5e1',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 1.5,
  };

  const headerCellSx = {
    color: '#94a3b8',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid rgba(255,255,255,0.10)',
    pb: 1,
  };

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 0.5 }}>
          Creación de Reportes
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Descargá los reportes del ranking generado. Cada PDF se crea al momento de la descarga.
        </Typography>
      </Box>

      {/* Tabla de reportes */}
      <TableContainer
        sx={{
          background: 'rgba(15, 23, 42, 0.5)',
          border: '1px solid rgba(255,255,255,0.06)',
          mb: 3,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Nombre</TableCell>
              <TableCell sx={headerCellSx}>Descripción</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'center', width: 80 }}>PDF</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {REPORTES.map((reporte) => {
              const state = downloadState[reporte.id];
              return (
                <TableRow
                  key={reporte.id}
                  sx={{
                    '&:hover': { background: 'rgba(255,255,255,0.03)' },
                    transition: 'background 0.15s',
                  }}
                >
                  <TableCell sx={{ ...cellSx, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {reporte.nombre}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: '#94a3b8', fontSize: '0.85rem' }}>
                    {reporte.descripcion}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                    {state === 'loading' ? (
                      <CircularProgress size={20} sx={{ color: '#60a5fa' }} />
                    ) : state === 'done' ? (
                      <CheckCircleOutlineIcon sx={{ color: '#10b981', fontSize: 22 }} />
                    ) : (
                      <Tooltip title={`Descargar ${reporte.nombre}`} placement="left">
                        <IconButton
                          size="small"
                          disabled={!rkgId}
                          onClick={() => handleDownload(reporte.id)}
                          sx={{
                            color: '#60a5fa',
                            '&:hover': { color: '#93c5fd', background: 'rgba(96,165,250,0.10)' },
                            '&.Mui-disabled': { color: '#334155' },
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ borderColor: '#334155', color: '#94a3b8', '&:hover': { borderColor: '#94a3b8' } }}
        >
          Anterior
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={onSalir}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
          }}
        >
          Finalizar
        </Button>
      </Box>
    </Box>
  );
}
